import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';
import { v4 as uuidv4 } from 'uuid';
import { usePOCreation } from '../contextProvider/POCreationContext';
import CustomDatePicker from '../../../../Components/Utility/DatePicker/DatePicker';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import * as SystemMastersAction from '../../../SystemMasters/Store/SystemMasters.action';
import '../../../Library/PurchaseOrderTemplate/POTemplate.css';

const PACK_TYPE_SINGLE = 'single-packed';
const PACK_TYPE_MULTI_SIZE = 'multi-size-packed';

const SPANNED_FIELDS = ['packType', 'productNo', 'colourWay', 'skuNumber', 'packs', 'quantity', 'approvedByDate', 'exFactoryDate', 'shipmentDate'];
const SHARED_FIELDS = ['packs', 'quantity', 'location', 'approvedByDate', 'exFactoryDate', 'shipmentDate'];

const SELECT_LABEL = 'Select';
const ENTER_LABEL = 'Enter';
const EMPTY_DISPLAY = '--';
const OBJECT_ID_FIELDS = ['productNo', 'colourWay', 'size', 'length'];

const generateParentRowId = () => `parent-${uuidv4()}`;
const generateParentRowKey = () => `prk-${uuidv4()}`;
const generateSubRowId = (parentId) => `${parentId}-sub-${uuidv4()}`;

function OrderDetailsForPO({ isPreviewMode = false, highlightProductInfo = null, isEditMode = false }) {
  const { t } = useTranslation('purchase-order');
  const { creationState, handlePOFieldChange, poProductDetails, isFieldsDisabledByStatus, currencySymbol } = usePOCreation();
  const dispatch = useDispatch();

  const isSupplierLoggedIn = useSelector((state) => state.UserDetails?.isSupplierLoggedIn);

  const filteredDevelopmentOrders = useSelector((state) => state.productPurchaseOrderReducer?.filteredDevelopmentOrders || []);
  const systemMastersDetails = useSelector((state) => state.SystemMastersDetails || {});
  const userInfo = useSelector((state) => state.UserDetails?.userInfo || {});
  const lengthVisibleFromAPI = useSelector((state) => state.SystemMastersDetails?.lengthVisible);
  const editedDevOrderData = useSelector((state) => state.Measuremets?.editedDevOrderData);

  const { poSizeList = [], lengthsList = [] } = systemMastersDetails || {};

  const availableProducts = useMemo(() => {
    return filteredDevelopmentOrders || [];
  }, [filteredDevelopmentOrders]);

  const productOptions = useMemo(() => {
    return availableProducts.map((order) => ({
      value: order._id,
      label: order.number || order.productNumber || `Product ${order._id}`,
    }));
  }, [availableProducts]);

  const [productColourways, setProductColourways] = useState({});
  const [sizeOptions, setSizeOptions] = useState([]);
  const [lengthOptions, setLengthOptions] = useState([]);
  const [lengthEnabled, setLengthEnabled] = useState(true);
  const [locationOptions, setLocationOptions] = useState([]);
  const [selectedDistributionCenters, setSelectedDistributionCenters] = useState({});
  const [autoSelectedRows, setAutoSelectedRows] = useState(new Set());
  const [manuallyClearedRows, setManuallyClearedRows] = useState(new Set());
  const [manuallySelectedProducts, setManuallySelectedProducts] = useState(new Set());
  const [lastNormalizedHash, setLastNormalizedHash] = useState('');
  const [activeDropdownKey, setActiveDropdownKey] = useState(null);

  useEffect(() => {
    dispatch(SystemMastersAction.getLengthAccess());
  }, [dispatch]);

  useEffect(() => {
    if (lengthVisibleFromAPI !== undefined) {
      setLengthEnabled(lengthVisibleFromAPI);
    } else if (userInfo?.lengthVisible !== undefined) {
      setLengthEnabled(userInfo.lengthVisible);
    }
  }, [lengthVisibleFromAPI, userInfo?.lengthVisible]);

  const fetchSystemMastersData = useCallback(async () => {
    if (userInfo?.userId) {
      const params = { loggedInUserId: userInfo.userId };

      if (poSizeList.length === 0) {
        await dispatch(SystemMastersAction.getPOSizeDetails(params));
      }

      if (lengthsList?.length === 0) {
        await dispatch(SystemMastersAction.getLengthsDetails(params));
      }
    }
  }, [dispatch, userInfo?.userId, poSizeList.length, lengthsList.length]);

  useEffect(() => {
    fetchSystemMastersData();
  }, [fetchSystemMastersData]);

  useEffect(() => {
    if (Array.isArray(poSizeList) && poSizeList.length > 0) {
      const activeSizes = poSizeList.filter((size) => size.isActive);
      const sizeRangeSizes = editedDevOrderData?.sizeRangeSizes || [];
      const sizeSelectOptions = activeSizes
        .filter((size) => sizeRangeSizes?.includes(size._id?.toString()))
        .map((size) => ({
          value: size._id,
          label: size?.name,
          hasLength: size?.hasLength,
        }));
      setSizeOptions(sizeSelectOptions);
    }
  }, [poSizeList, editedDevOrderData]);

  useEffect(() => {
    if (Array.isArray(lengthsList) && lengthsList.length > 0) {
      const activeLengths = lengthsList.filter((length) => length.isActive);
      const lengthSelectOptions = activeLengths.map((length) => ({
        value: length._id,
        label: length.name,
      }));
      setLengthOptions(lengthSelectOptions);
    }
  }, [lengthsList]);

  useEffect(() => {
    const distributionCentres = poProductDetails?.companyDetails?.distributionCentres || [];
    const locationSelectOptions = distributionCentres.map((center, index) => ({
      value: center.distributionName || `distribution-${index}`,
      label: center.distributionName || `Distribution Center ${index + 1}`,
      distributionData: center,
    }));
    setLocationOptions(locationSelectOptions);
  }, [poProductDetails?.companyDetails?.distributionCentres]);

  const extractColourways = (productDetails) => {
    let colourways = [];

    if (productDetails.colourwayLibraries && productDetails.colourwayLibraries.length > 0) {
      colourways = productDetails.colourwayLibraries.map((cw) => ({
        value: cw._id,
        label: `${cw.name} | ${cw.newReferences || ''}`,
      }));
    }

    return colourways;
  };

  const getColourwaysForProduct = useCallback(
    (productId, rowIdx) => {
      const selectedProduct = availableProducts.find((order) => order._id === productId);

      if (selectedProduct) {
        const colourways = extractColourways(selectedProduct);

        setProductColourways((prev) => ({
          ...prev,
          [rowIdx]: colourways,
        }));

        if (colourways.length === 1) {
          const currentOrderDetails = [...(creationState?.orderDetails || [])];
          const row = currentOrderDetails[rowIdx];
          const shouldAutoFill = !isEditMode || row?.isNewlyAdded;
          if (row && !row.colourWay && shouldAutoFill) {
            currentOrderDetails[rowIdx].colourWay = colourways[0].value;
            handlePOFieldChange('orderDetails', currentOrderDetails);
          }
        }
      } else {
        setProductColourways((prev) => ({
          ...prev,
          [rowIdx]: [],
        }));
      }
    },
    [availableProducts, creationState?.orderDetails, handlePOFieldChange, isEditMode],
  );

  useEffect(() => {
    const currentOrderDetails = creationState?.orderDetails || [];
    const devOrderId = creationState?.devOrderId;

    if (currentOrderDetails.length > 0 && devOrderId && availableProducts.length > 0 && !creationState.isRefreshingAfterSave) {
      let hasChanges = false;
      const updatedOrderDetails = currentOrderDetails.map((row, rowIdx) => {
        const updatedRow = { ...row };
        const rowKey = row.id || `row-${rowIdx}`;

        if (manuallyClearedRows.has(rowKey)) {
          return updatedRow;
        }

        if (manuallySelectedProducts.has(rowKey)) {
          return updatedRow;
        }

        if (isEditMode && !row?.isNewlyAdded && row.productNo) {
          return updatedRow;
        }

        const matchingProduct = availableProducts.find((order) => order._id === devOrderId);
        if (!matchingProduct) {
          return updatedRow;
        }

        const currentProductNo = updatedRow.productNo || '';
        const isEmpty = !currentProductNo || currentProductNo === '';
        const isDifferentProduct = currentProductNo && currentProductNo !== devOrderId;
        const wasNotAutoSelected = !autoSelectedRows.has(rowKey);

        if (isEmpty || (isDifferentProduct && wasNotAutoSelected)) {
          updatedRow.productNo = matchingProduct._id;
          hasChanges = true;
          setAutoSelectedRows((prev) => new Set(prev).add(rowKey));
          setTimeout(() => getColourwaysForProduct(matchingProduct._id, rowIdx), 0);
        }

        return updatedRow;
      });

      if (hasChanges) {
        handlePOFieldChange('orderDetails', updatedOrderDetails);
      }
    }
  }, [
    creationState?.orderDetails,
    creationState?.devOrderId,
    creationState.isRefreshingAfterSave,
    availableProducts,
    handlePOFieldChange,
    getColourwaysForProduct,
    autoSelectedRows,
    manuallyClearedRows,
    manuallySelectedProducts,
    isEditMode,
  ]);

  useEffect(() => {
    const currentOrderDetails = creationState?.orderDetails || [];

    if (currentOrderDetails.length > 0 && availableProducts.length > 0) {
      currentOrderDetails.forEach((row, rowIdx) => {
        if (row.productNo && !productColourways[rowIdx]) {
          getColourwaysForProduct(row.productNo, rowIdx);
        }
      });
    }
  }, [availableProducts, creationState?.orderDetails, getColourwaysForProduct, productColourways]);

  useEffect(() => {
    if (highlightProductInfo && creationState?.orderDetails?.length > 0) {
      const matchingRowIndex = creationState.orderDetails.findIndex((row) => {
        return (
          (!highlightProductInfo.productNumber || row.productNumber === highlightProductInfo.productNumber) &&
          (!highlightProductInfo.colourway || row.colourway === highlightProductInfo.colourway) &&
          (!highlightProductInfo.size || row.size === highlightProductInfo.size) &&
          (!highlightProductInfo.length || row.length === highlightProductInfo.length)
        );
      });

      if (matchingRowIndex !== -1) {
        setTimeout(() => {
          const rowElement = document.querySelector(`[data-row-index="${matchingRowIndex}"]`);
          if (rowElement) {
            rowElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });

            rowElement.classList.add('highlighted-row');

            setTimeout(() => {
              rowElement.classList.remove('highlighted-row');
            }, 3000);
          }
        }, 500);
      }
    }
  }, [highlightProductInfo, creationState?.orderDetails]);

  useEffect(() => {
    const currentOrderDetails = creationState?.orderDetails || [];

    if (currentOrderDetails.length > 0 && locationOptions.length > 0) {
      const newSelectedDistributionCenters = {};
      let hasChanges = false;

      currentOrderDetails.forEach((row, rowIdx) => {
        if (row.location && !selectedDistributionCenters[rowIdx]) {
          const selectedLocation = locationOptions.find((option) => option.value === row.location);
          if (selectedLocation?.distributionData) {
            newSelectedDistributionCenters[rowIdx] = selectedLocation.distributionData;
            hasChanges = true;
          }
        }
      });

      if (hasChanges) {
        setSelectedDistributionCenters((prev) => ({
          ...prev,
          ...newSelectedDistributionCenters,
        }));
      }
    }
  }, [creationState?.orderDetails, locationOptions, selectedDistributionCenters]);

  useEffect(() => {
    const currentOrderDetails = creationState?.orderDetails || [];

    if (currentOrderDetails.length > 0 && (sizeOptions.length > 0 || lengthOptions.length > 0)) {
      let hasChanges = false;
      const updatedOrderDetails = currentOrderDetails.map((row) => {
        const updatedRow = { ...row };

        if (!updatedRow.size && sizeOptions.length === 1) {
          updatedRow.size = sizeOptions[0].value;
          hasChanges = true;
        }

        if (!updatedRow.length && lengthEnabled && lengthOptions.length === 1) {
          updatedRow.length = lengthOptions[0].value;
          hasChanges = true;
        }

        return updatedRow;
      });

      if (hasChanges) {
        handlePOFieldChange('orderDetails', updatedOrderDetails);
      }
    }
  }, [creationState?.orderDetails, sizeOptions, lengthOptions, lengthEnabled, handlePOFieldChange]);

  const columns = useMemo(
    () => [
      {
        key: 'packType',
        label: t('CREATE_EDIT_PO.PACK_TYPE'),
        type: 'select',
        options: [
          { value: PACK_TYPE_SINGLE, label: t('CREATE_EDIT_PO.SINGLE_PACKED') },
          { value: 'multiple-single-size-packed', label: t('CREATE_EDIT_PO.MULTIPLE_SINGLE_SIZE_PACKED') },
          { value: PACK_TYPE_MULTI_SIZE, label: t('CREATE_EDIT_PO.MULTI_SIZE_PACKED') },
          { value: 'multi-destination-packed', label: t('CREATE_EDIT_PO.MULTI_DESTINATION_PACKED') },
        ],
      },
      {
        key: 'productNo',
        label: t('CREATE_EDIT_PO.PRODUCT_NO'),
        type: 'select',
        options: productOptions,
      },
      { key: 'colourWay', label: t('CREATE_EDIT_PO.COLOURWAY'), type: 'select' },
      { key: 'skuNumber', label: t('CREATE_EDIT_PO.SKU_NUMBER'), type: 'text' },
      { key: 'size', label: t('CREATE_EDIT_PO.SIZE'), type: 'select', options: sizeOptions },
      ...(lengthEnabled ? [{ key: 'length', label: t('CREATE_EDIT_PO.LENGTH'), type: 'select', options: lengthOptions }] : []),
      { key: 'ratio', label: t('CREATE_EDIT_PO.RATIO'), type: 'number' },
      { key: 'packs', label: t('CREATE_EDIT_PO.PACKS'), type: 'number' },
      { key: 'quantity', label: t('CREATE_EDIT_PO.QUANTITY'), type: 'number' },
      { key: 'location', label: t('CREATE_EDIT_PO.LOCATION'), type: 'select', options: locationOptions },
      { key: 'unitPrice', label: `${t('CREATE_EDIT_PO.UNIT_PRICE')} (${currencySymbol})`, type: 'number' },
      { key: 'discount', label: `${t('CREATE_EDIT_PO.DISCOUNT')} (%)`, type: 'number', max: 100 },
      { key: 'priceAfterDiscount', label: `${t('CREATE_EDIT_PO.PRICE_AFTER_DISCOUNT')} (${currencySymbol})`, type: 'number' },
      { key: 'total', label: `${t('CREATE_EDIT_PO.TOTAL')} (${currencySymbol})`, type: 'number' },
      { key: 'approvedByDate', label: t('CREATE_EDIT_PO.APPROVED_BY_DATE'), type: 'date' },
      { key: 'exFactoryDate', label: t('CREATE_EDIT_PO.EX_FACTORY_DATE'), type: 'date' },
      { key: 'shipmentDate', label: t('CREATE_EDIT_PO.SHIPMENT_DATE'), type: 'date' },
      { key: 'action', label: '', type: 'action' },
    ],
    [t, productOptions, sizeOptions, lengthEnabled, lengthOptions, locationOptions, currencySymbol],
  );

  const orderDetails = useMemo(() => creationState?.orderDetails || [], [creationState?.orderDetails]);

  const orderDetailsHash = useMemo(() => {
    const currentOrderDetails = creationState?.orderDetails || [];
    const purchaseOrderId = creationState?.purchaseOrderId;

    const rowIds = currentOrderDetails
      .slice(0, 3)
      .map((row) => row.id || '')
      .join('-');
    return `${purchaseOrderId || 'new'}-${currentOrderDetails.length}-${rowIds}`;
  }, [creationState?.orderDetails, creationState?.purchaseOrderId]);

  const getId = useCallback((val) => {
    if (!val) return null;
    if (typeof val === 'object') {
      return val._id || val.id || val.value || null;
    }
    return val;
  }, []);

  const normalizeRowFields = useCallback((row, getIdFn, originalIndex) => {
    const next = { ...row };
    let changed = false;

    if (next.templateSequence === undefined && originalIndex !== undefined) {
      next.templateSequence = originalIndex;
      changed = true;
    }

    if (!next.id && next._id) {
      next.id = next._id;
      changed = true;
    }

    if (next.productNo == null && next.productNumber) {
      const newVal = getIdFn(next.productNumber);
      if (newVal !== next.productNo) {
        next.productNo = newVal;
        changed = true;
      }
    }

    if (next.colourWay == null && next.colourway != null) {
      const newVal = getIdFn(next.colourway);
      if (newVal !== next.colourWay) {
        next.colourWay = newVal;
        changed = true;
      }
    }

    if (!next.colourwayName && next.colourway && typeof next.colourway === 'object' && next.colourway.name) {
      next.colourwayName = next.colourway.name;
      changed = true;
    }
    if (!next.sizeName && next.size && typeof next.size === 'object' && next.size !== null && next.size.name) {
      next.sizeName = next.size.name;
      changed = true;
    }
    if (!next.lengthName && next.length && typeof next.length === 'object' && next.length !== null && next.length.name) {
      next.lengthName = next.length.name;
      changed = true;
    }

    if (typeof next.size === 'object' && next.size !== null) {
      const newVal = getIdFn(next.size);
      if (newVal !== next.size) {
        next.size = newVal;
        changed = true;
      }
    }
    if (typeof next.length === 'object' && next.length !== null) {
      const newVal = getIdFn(next.length);
      if (newVal !== next.length) {
        next.length = newVal;
        changed = true;
      }
    }

    if (next.discount == null && next.discountPercent != null) {
      next.discount = Number(next.discountPercent) || null;
      changed = true;
    }

    if (row.isSubRow !== undefined && next.isSubRow !== row.isSubRow) {
      next.isSubRow = row.isSubRow;
      changed = true;
    }
    if (row.parentRowKey !== undefined && next.parentRowKey !== row.parentRowKey) {
      next.parentRowKey = row.parentRowKey;
      changed = true;
    }
    if (row.parentRowId !== undefined && next.parentRowId !== row.parentRowId) {
      next.parentRowId = row.parentRowId;
      changed = true;
    }

    return { normalizedRow: next, hasChanges: changed };
  }, []);

  const groupMultiSizeRows = useCallback((normalized) => {
    const grouped = [];
    const processed = new Set();
    let changed = false;

    const multiSizeParents = [];
    normalized.forEach((row, index) => {
      if (row.packType === PACK_TYPE_MULTI_SIZE && !row.isSubRow) {
        multiSizeParents.push({ row, index });
      }
    });

    multiSizeParents.forEach(({ row, index }) => {
      if (processed.has(index)) return;

      const parentId = row.id || generateParentRowId();
      const parentRowKey = row.parentRowKey || generateParentRowKey();

      const parentRow = {
        ...row,
        id: parentId,
        parentRowKey,
        isSubRow: false,
        parentRowId: null,
      };
      grouped.push(parentRow);
      processed.add(index);
      changed = true;

      normalized.forEach((candidateRow, candidateIndex) => {
        if (processed.has(candidateIndex) || candidateIndex === index) return;

        if (candidateRow.packType === PACK_TYPE_MULTI_SIZE && !candidateRow.isSubRow && !candidateRow.parentRowId) return;

        const isLinkedByParentRowKey = candidateRow.parentRowId && candidateRow.parentRowId === parentRowKey;
        const isExplicitSubRow = candidateRow.isSubRow && isLinkedByParentRowKey;

        if (isLinkedByParentRowKey || isExplicitSubRow) {
          const subRowId = candidateRow.id || generateSubRowId(parentId);
          const subRow = {
            ...candidateRow,
            id: subRowId,
            isSubRow: true,
            parentRowId: parentRowKey,
          };
          grouped.push(subRow);
          processed.add(candidateIndex);
          changed = true;
        }
      });
    });

    normalized.forEach((row, index) => {
      if (processed.has(index)) return;

      const parentId = row.id || generateParentRowId();

      const regularRow = {
        ...row,
        id: parentId,
        isSubRow: false,
        parentRowId: null,
        parentRowKey: row.packType === PACK_TYPE_MULTI_SIZE ? row.parentRowKey : null,
      };

      grouped.push(regularRow);
      processed.add(index);
      changed = true;
    });

    return { groupedRows: grouped, hasChanges: changed };
  }, []);

  useEffect(() => {
    const rows = creationState?.orderDetails || [];

    if (
      !Array.isArray(rows) ||
      rows.length === 0 ||
      creationState.validationErrors?.orderDetails ||
      creationState.validationErrors?.orderDetailsProducts ||
      creationState.isRefreshingAfterSave
    ) {
      return;
    }

    if (orderDetailsHash === lastNormalizedHash) {
      return;
    }

    let overallChanged = false;

    const normalized = rows.map((row, index) => {
      const { normalizedRow, hasChanges } = normalizeRowFields(row, getId, index);
      if (hasChanges) overallChanged = true;
      return normalizedRow;
    });

    const hasExistingStructure = normalized.some(
      (row) =>
        (row.isSubRow && row.parentRowId) ||
        (row.packType === PACK_TYPE_MULTI_SIZE &&
          !row.isSubRow &&
          row.parentRowKey &&
          normalized.some((subRow) => subRow.isSubRow && subRow.parentRowId && subRow.parentRowId === row.parentRowKey)),
    );

    let finalRows = normalized;
    if (!hasExistingStructure) {
      const { groupedRows, hasChanges } = groupMultiSizeRows(normalized);
      finalRows = groupedRows;
      if (hasChanges) overallChanged = true;
    }

    const parentMap = new Map();
    finalRows.forEach((row) => {
      if (!row.isSubRow && row.parentRowKey) {
        parentMap.set(row.parentRowKey, row);
      }
    });

    finalRows.sort((a, b) => {
      if (a.isSubRow && b.isSubRow && a.parentRowId === b.parentRowId) {
        const aSeq = a.templateSequence !== undefined ? a.templateSequence : Infinity;
        const bSeq = b.templateSequence !== undefined ? b.templateSequence : Infinity;
        return aSeq - bSeq;
      }

      if (a.isSubRow && a.parentRowId && !b.isSubRow && b.parentRowKey === a.parentRowId) {
        return 1;
      }
      if (b.isSubRow && b.parentRowId && !a.isSubRow && a.parentRowKey === b.parentRowId) {
        return -1;
      }

      if (a.isSubRow && b.isSubRow && a.parentRowId !== b.parentRowId) {
        const aParent = parentMap.get(a.parentRowId);
        const bParent = parentMap.get(b.parentRowId);
        const aParentSeq = aParent?.templateSequence !== undefined ? aParent.templateSequence : Infinity;
        const bParentSeq = bParent?.templateSequence !== undefined ? bParent.templateSequence : Infinity;
        if (aParentSeq !== bParentSeq) {
          return aParentSeq - bParentSeq;
        }
        const aSeq = a.templateSequence !== undefined ? a.templateSequence : Infinity;
        const bSeq = b.templateSequence !== undefined ? b.templateSequence : Infinity;
        return aSeq - bSeq;
      }

      if (a.templateSequence !== undefined && b.templateSequence !== undefined) {
        return a.templateSequence - b.templateSequence;
      }
      if (a.templateSequence !== undefined) return -1;
      if (b.templateSequence !== undefined) return 1;
      return 0;
    });

    if (overallChanged) {
      handlePOFieldChange('orderDetails', finalRows);
    }

    setLastNormalizedHash(orderDetailsHash);
  }, [
    creationState?.orderDetails,
    creationState?.purchaseOrderId,
    orderDetailsHash,
    lastNormalizedHash,
    handlePOFieldChange,
    creationState.validationErrors,
    creationState.isRefreshingAfterSave,
    normalizeRowFields,
    groupMultiSizeRows,
    getId,
  ]);

  const handleDropdownOpen = (rowIdx, colKey) => {
    setActiveDropdownKey(`${rowIdx}-${colKey}`);
  };

  const handleDropdownClose = useCallback(() => {
    setActiveDropdownKey(null);
  }, []);

  const getUIDisplayData = useMemo(() => {
    const grouped = [];
    const processed = new Set();

    const findSubRows = (parentRow, parentIndex) => {
      const subRows = [];
      const { parentRowKey } = parentRow;

      orderDetails.forEach((candidate, candidateIndex) => {
        if (processed.has(candidateIndex) || candidateIndex === parentIndex) {
          return;
        }

        if (!candidate.isSubRow) {
          return;
        }

        const isLinkedByParentRowKey = parentRowKey && candidate.parentRowId && candidate.parentRowId === parentRowKey;

        if (isLinkedByParentRowKey) {
          subRows.push({ ...candidate, originalIndex: candidateIndex });
          processed.add(candidateIndex);
        }
      });
      return subRows;
    };

    orderDetails.forEach((row, index) => {
      if (processed.has(index) || row.isSubRow) {
        return;
      }

      const parentId = row.id || `parent-auto-${index}`;
      const parentData = { ...row, id: parentId, originalIndex: index };
      if (row.packType === PACK_TYPE_MULTI_SIZE && !row.isSubRow) {
        const subRows = findSubRows(row, index);
        parentData.subRows = subRows;
        const parentOwnQuantity = (Number(row.ratio) || 0) * (Number(row.packs) || 0);
        const subRowsQuantity = (subRows || []).reduce((sum, sr) => sum + (Number(sr.quantity) || 0), 0);
        parentData.quantity = parentOwnQuantity + subRowsQuantity;
        // parentData.quantity = parentOwnQuantity;
      }

      grouped.push(parentData);
      processed.add(index);
    });

    return grouped;
  }, [orderDetails]);

  const getRowSpan = useCallback((row) => {
    if (row.packType === PACK_TYPE_MULTI_SIZE && !row.isSubRow) {
      return (row.subRows?.length || 0) + 1;
    }
    return 1;
  }, []);

  const isFieldDisabled = useCallback(
    (row, colKey) => {
      if (colKey !== 'action' && isPreviewMode) return true;

      if (colKey !== 'action' && isSupplierLoggedIn) return true;

      if (colKey !== 'action' && isFieldsDisabledByStatus()) return true;

      if (colKey === 'priceAfterDiscount' || colKey === 'total') return true;

      if (row?.isSubRow && SHARED_FIELDS.includes(colKey)) {
        return true;
      }

      if (isEditMode && !row?.isNewlyAdded) {
        const editModeDisabledFields = ['packType', 'productNo', 'colourWay', 'skuNumber', 'size'];
        if (editModeDisabledFields.includes(colKey)) {
          return true;
        }
      }

      const packType = row?.packType;
      switch (packType) {
        case PACK_TYPE_SINGLE:
          return ['ratio', 'packs', 'location'].includes(colKey);
        case 'multiple-single-size-packed':
          return ['quantity', 'location'].includes(colKey);
        case PACK_TYPE_MULTI_SIZE:
          return row?.isSubRow ? false : ['quantity', 'location'].includes(colKey);
        case 'multi-destination-packed':
          return ['quantity'].includes(colKey);
        default:
          return false;
      }
    },
    [isFieldsDisabledByStatus, isPreviewMode, isSupplierLoggedIn, isEditMode],
  );

  const calculatePriceAfterDiscount = useCallback((unitPrice, discountPercent) => {
    const safeUnit = Number(unitPrice) || 0;
    const safeDiscount = Number(discountPercent) || 0;
    const discounted = safeUnit - (safeUnit * safeDiscount) / 100;
    return Number(discounted.toFixed(2));
  }, []);

  /**
   * Calculate total for a row based on pack type:
   * - single-packed: quantity * priceAfterDiscount
   * - multi-size-packed: ratio * packs * priceAfterDiscount (for each sub-row)
   * - other pack types: ratio * packs * priceAfterDiscount
   */
  const calculateTotal = useCallback((row) => {
    const priceAfterDiscount = Number(row?.priceAfterDiscount || 0);
    const packType = row?.packType;
    const packs = Number(row?.packs || 0);
    const ratio = Number(row?.ratio || 0);
    const quantity = Number(row?.quantity || 0);

    let total = 0;

    if (packType === PACK_TYPE_SINGLE) {
      total = Number((quantity * priceAfterDiscount).toFixed(2));
    } else if (packType === PACK_TYPE_MULTI_SIZE) {
      total = Number((ratio * packs * priceAfterDiscount).toFixed(2));
    } else {
      total = Number((ratio * packs * priceAfterDiscount).toFixed(2));
    }

    return total;
  }, []);

  const actionCol = (colKey) => {
    if (colKey === 'shipmentDate' && isPreviewMode) {
      return 'unset-css';
    }
    return colKey === 'action' ? 'last-fix-col' : '';
  };

  const addRow = useCallback(() => {
    const currentOrderDetails = creationState?.orderDetails || [];

    const uniqueId = generateParentRowId();
    const uniqueParentRowKey = generateParentRowKey();

    const maxSequence = currentOrderDetails.reduce((max, row) => {
      const seq = row.templateSequence !== undefined ? row.templateSequence : -1;
      return Math.max(max, seq);
    }, -1);
    const newSequence = maxSequence + 1;

    const newRow = columns.reduce(
      (acc, col) => {
        if (col.key !== 'action') {
          const isObjectIdField = ['productNo', 'colourWay', 'size', 'length'].includes(col.key);
          if (col.type === 'number') {
            acc[col.key] = null;
          } else if (isObjectIdField) {
            acc[col.key] = null;
          } else if (col.key === 'approvedByDate' || col.key === 'exFactoryDate' || col.key === 'shipmentDate') {
            acc[col.key] = null;
          } else {
            acc[col.key] = '';
          }
        }
        return acc;
      },
      {
        id: uniqueId,
        parentRowKey: uniqueParentRowKey,
        isSubRow: false,
        parentRowId: null,
        templateSequence: newSequence,
        isNewlyAdded: true, // Flag to identify newly added rows in edit mode
      },
    );

    const devOrderId = creationState?.devOrderId;
    if (devOrderId && availableProducts.length > 0) {
      const matchingProduct = availableProducts.find((order) => order._id === devOrderId);
      if (matchingProduct) {
        newRow.productNo = matchingProduct._id;
      }
    }

    columns.forEach((col) => {
      if (col.type === 'select' && col.options && col.options.length === 1 && !newRow[col.key]) {
        newRow[col.key] = col.options[0].value;
      }
    });

    if (sizeOptions.length === 1 && !newRow.size) {
      newRow.size = sizeOptions[0].value;
    }
    if (lengthEnabled && lengthOptions.length === 1 && !newRow.length) {
      newRow.length = lengthOptions[0].value;
    }

    if (newRow.packType === PACK_TYPE_MULTI_SIZE && !newRow.packs) {
      newRow.packs = 1;
    }

    const updatedOrderDetails = [...currentOrderDetails, newRow];

    handlePOFieldChange('orderDetails', updatedOrderDetails);

    if (newRow.productNo) {
      const newRowIndex = updatedOrderDetails.length - 1;
      getColourwaysForProduct(newRow.productNo, newRowIndex);
    }
  }, [
    columns,
    creationState?.orderDetails,
    creationState?.devOrderId,
    availableProducts,
    handlePOFieldChange,
    getColourwaysForProduct,
    sizeOptions,
    lengthOptions,
    lengthEnabled,
  ]);

  const deleteRow = useCallback(
    (rowIdx) => {
      const currentOrderDetails = creationState?.orderDetails || [];
      const rowToDelete = currentOrderDetails[rowIdx];

      let updatedOrderDetails;

      if (rowToDelete && !rowToDelete.isSubRow && rowToDelete.packType === PACK_TYPE_MULTI_SIZE) {
        const { parentRowKey } = rowToDelete;
        const subRows = currentOrderDetails.filter((row) => row.isSubRow && row.parentRowId === parentRowKey);

        if (subRows.length > 0) {
          const firstSubRow = subRows[0];
          const promotedParent = {
            ...firstSubRow,
            isSubRow: false,
            parentRowId: null,
            parentRowKey,
            packs: rowToDelete.packs || firstSubRow.packs || 1,
            quantity: rowToDelete.quantity || firstSubRow.quantity || 0,
            location: rowToDelete.location || firstSubRow.location || '',
            approvedByDate: rowToDelete.approvedByDate || firstSubRow.approvedByDate,
            exFactoryDate: rowToDelete.exFactoryDate || firstSubRow.exFactoryDate,
            shipmentDate: rowToDelete.shipmentDate || firstSubRow.shipmentDate,
          };

          promotedParent.total = calculateTotal(promotedParent);

          updatedOrderDetails = currentOrderDetails.filter((row, index) => {
            return index !== rowIdx && !(row.id === firstSubRow.id);
          });
          updatedOrderDetails.splice(rowIdx, 0, promotedParent);
        } else {
          updatedOrderDetails = currentOrderDetails.filter((_, index) => index !== rowIdx);
        }
      } else {
        updatedOrderDetails = currentOrderDetails.filter((_, index) => index !== rowIdx);
      }

      handlePOFieldChange('orderDetails', updatedOrderDetails);

      const rowKey = rowToDelete?.id || `row-${rowIdx}`;
      setAutoSelectedRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rowKey);
        return newSet;
      });
      setManuallyClearedRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rowKey);
        return newSet;
      });
      setManuallySelectedProducts((prev) => {
        const newSet = new Set(prev);
        newSet.delete(rowKey);
        return newSet;
      });

      if (rowToDelete && !rowToDelete.isSubRow && rowToDelete.packType === PACK_TYPE_MULTI_SIZE) {
        const { parentRowKey } = rowToDelete;
        const subRows = currentOrderDetails.filter((row) => row.isSubRow && row.parentRowId === parentRowKey);
        subRows.forEach((subRow) => {
          const subRowKey = subRow.id || `row-${currentOrderDetails.indexOf(subRow)}`;
          setAutoSelectedRows((prev) => {
            const newSet = new Set(prev);
            newSet.delete(subRowKey);
            return newSet;
          });
          setManuallyClearedRows((prev) => {
            const newSet = new Set(prev);
            newSet.delete(subRowKey);
            return newSet;
          });
          setManuallySelectedProducts((prev) => {
            const newSet = new Set(prev);
            newSet.delete(subRowKey);
            return newSet;
          });
        });
      }

      setProductColourways((prev) => {
        const newState = { ...prev };
        delete newState[rowIdx];
        Object.keys(newState).forEach((key) => {
          const index = parseInt(key, 10);
          if (index > rowIdx) {
            newState[index - 1] = newState[index];
            delete newState[index];
          }
        });

        return newState;
      });

      setSelectedDistributionCenters((prev) => {
        const newState = { ...prev };
        delete newState[rowIdx];
        Object.keys(newState).forEach((key) => {
          const index = parseInt(key, 10);
          if (index > rowIdx) {
            newState[index - 1] = newState[index];
            delete newState[index];
          }
        });

        return newState;
      });
    },
    [creationState?.orderDetails, handlePOFieldChange],
  );

  const addSubRow = useCallback(
    (parentRowIdx) => {
      const currentOrderDetails = creationState?.orderDetails || [];
      const parentRow = currentOrderDetails[parentRowIdx];

      if (!parentRow || parentRow.packType !== PACK_TYPE_MULTI_SIZE || parentRow.isSubRow) {
        return;
      }
      let ensuredParentId = parentRow.id;
      let ensuredParentRowKey = parentRow.parentRowKey;

      if (!ensuredParentId) {
        ensuredParentId = generateParentRowId();
      }
      if (!ensuredParentRowKey) {
        ensuredParentRowKey = generateParentRowKey();
      }

      const parentUpdated = {
        ...parentRow,
        id: ensuredParentId,
        parentRowKey: ensuredParentRowKey,
        isSubRow: false,
        parentRowId: null,
      };
      currentOrderDetails[parentRowIdx] = parentUpdated;

      const subRowId = generateSubRowId(ensuredParentId);

      const subRowSequence = parentRow.templateSequence !== undefined ? parentRow.templateSequence : null;

      const newSubRow = {
        id: subRowId,
        isSubRow: true,
        parentRowId: ensuredParentRowKey,
        packType: parentRow.packType,
        templateSequence: subRowSequence,
        isNewlyAdded: true, // Flag to identify newly added sub-rows in edit mode
        size: null,
        length: null,
        ratio: null,
        unitPrice: null,
        discount: null,
        priceAfterDiscount: null,
        total: null,

        productNo: parentRow.productNo,
        colourWay: parentRow.colourWay,
        skuNumber: parentRow.skuNumber,
        packs: parentRow.packs ?? null,
        quantity: parentRow.quantity || null,
        location: parentRow.location || '',
        approvedByDate: parentRow.approvedByDate || null,
        exFactoryDate: parentRow.exFactoryDate || null,
        shipmentDate: parentRow.shipmentDate || null,
      };

      newSubRow.total = calculateTotal(newSubRow);
      let insertIndex = parentRowIdx + 1;
      while (
        insertIndex < currentOrderDetails.length &&
        currentOrderDetails[insertIndex].isSubRow &&
        currentOrderDetails[insertIndex].parentRowId === ensuredParentRowKey
      ) {
        insertIndex += 1;
      }

      const updatedOrderDetails = [...currentOrderDetails.slice(0, insertIndex), newSubRow, ...currentOrderDetails.slice(insertIndex)];

      handlePOFieldChange('orderDetails', updatedOrderDetails);
    },
    [creationState?.orderDetails, handlePOFieldChange, calculateTotal],
  );

  const deleteSubRow = useCallback(
    (rowIdx) => {
      const currentOrderDetails = creationState?.orderDetails || [];
      const rowToDelete = currentOrderDetails[rowIdx];
      const updatedOrderDetails = currentOrderDetails.filter((_, index) => index !== rowIdx);

      if (rowToDelete) {
        const rowKey = rowToDelete.id || `row-${rowIdx}`;
        setAutoSelectedRows((prev) => {
          const newSet = new Set(prev);
          newSet.delete(rowKey);
          return newSet;
        });
        setManuallyClearedRows((prev) => {
          const newSet = new Set(prev);
          newSet.delete(rowKey);
          return newSet;
        });
      }

      handlePOFieldChange('orderDetails', updatedOrderDetails);
    },
    [creationState?.orderDetails, handlePOFieldChange],
  );

  const validateMultiSizeSubRowValues = useCallback(
    (currentOrderDetails) => {
      let hasChanges = false;

      currentOrderDetails.forEach((row) => {
        if (row.packType === PACK_TYPE_MULTI_SIZE && !row.isSubRow) {
          const { parentRowKey } = row;
          if (!parentRowKey) return;

          currentOrderDetails.forEach((subRow, subIdx) => {
            if (subRow.isSubRow && subRow.parentRowId === parentRowKey) {
              if (!subRow.packs) {
                const parentPacks = row.packs ?? null;
                currentOrderDetails[subIdx].packs = parentPacks;
                hasChanges = true;
              }

              if (hasChanges) {
                const correctedTotal = calculateTotal(currentOrderDetails[subIdx]);
                currentOrderDetails[subIdx].total = correctedTotal;
              }
            }
          });
        }
      });

      return hasChanges;
    },
    [calculateTotal],
  );

  const updateParentRowQuantityForMultiSize = useCallback((currentOrderDetails, changedRowIdx) => {
    const changedRow = currentOrderDetails[changedRowIdx];
    if (!changedRow) return;

    let parentRowIdx = null;
    let parentRowKey = null;

    if (changedRow.isSubRow && changedRow.parentRowId) {
      parentRowKey = changedRow.parentRowId;
      parentRowIdx = currentOrderDetails.findIndex((row) => row.parentRowKey === parentRowKey && !row.isSubRow);
    } else if (changedRow.packType === PACK_TYPE_MULTI_SIZE && !changedRow.isSubRow && changedRow.parentRowKey) {
      parentRowKey = changedRow.parentRowKey;
      parentRowIdx = changedRowIdx;
    }

    if (parentRowIdx === null || parentRowIdx === -1) return;

    const parentRow = currentOrderDetails[parentRowIdx];
    if (!parentRow || parentRow.packType !== PACK_TYPE_MULTI_SIZE) return;

    const parentRatio = Number(parentRow.ratio || 0);
    const parentPacks = Number(parentRow.packs || 0);
    const parentOwnQuantity = parentRatio * parentPacks;

    currentOrderDetails[parentRowIdx].quantity = parentOwnQuantity;
  }, []);

  const updateSubRow = useCallback(
    (rowIdx, field, value) => {
      const currentOrderDetails = [...(creationState?.orderDetails || [])];
      if (currentOrderDetails[rowIdx]) {
        currentOrderDetails[rowIdx][field] = value;

        if (field === 'unitPrice' || field === 'discount') {
          const unit = Number(currentOrderDetails[rowIdx].unitPrice || 0);
          const disc = Number(currentOrderDetails[rowIdx].discount || 0);
          const priceAfterDiscount = calculatePriceAfterDiscount(unit, disc);
          currentOrderDetails[rowIdx].priceAfterDiscount = priceAfterDiscount;

          const total = calculateTotal(currentOrderDetails[rowIdx]);
          currentOrderDetails[rowIdx].total = total;
        }

        if (field === 'ratio' || field === 'packs') {
          let correctedValue = value;
          if (field === 'packs' && (value === 0 || !value)) {
            correctedValue = 1;
            currentOrderDetails[rowIdx][field] = correctedValue;
          }

          const currentRow = currentOrderDetails[rowIdx];
          const parentPacks =
            currentRow.isSubRow && currentRow.parentRowId
              ? currentOrderDetails.find((row) => row.parentRowKey === currentRow.parentRowId && !row.isSubRow)?.packs || currentRow.packs
              : currentRow.packs;

          const ratio = Number(currentOrderDetails[rowIdx].ratio || 0);
          const packs = Number(parentPacks || 0);
          currentOrderDetails[rowIdx].quantity = ratio * packs;

          const total = calculateTotal(currentOrderDetails[rowIdx]);
          currentOrderDetails[rowIdx].total = total;

          updateParentRowQuantityForMultiSize(currentOrderDetails, rowIdx);
        }

        validateMultiSizeSubRowValues(currentOrderDetails);

        handlePOFieldChange('orderDetails', currentOrderDetails);
      }
    },
    [
      creationState?.orderDetails,
      handlePOFieldChange,
      calculatePriceAfterDiscount,
      calculateTotal,
      validateMultiSizeSubRowValues,
      updateParentRowQuantityForMultiSize,
    ],
  );

  const handlePackTypeChange = useCallback(
    (currentOrderDetails, rowIdx, value) => {
      const previousPackType = currentOrderDetails[rowIdx].packType;
      currentOrderDetails[rowIdx].packType = value;

      if (previousPackType === PACK_TYPE_MULTI_SIZE && value !== PACK_TYPE_MULTI_SIZE) {
        const { parentRowKey } = currentOrderDetails[rowIdx];
        if (parentRowKey) {
          const subRowIndices = [];
          currentOrderDetails.forEach((row, idx) => {
            if (row.isSubRow && row.parentRowId === parentRowKey) {
              subRowIndices.push(idx);
            }
          });
          subRowIndices.reverse().forEach((subRowIdx) => {
            currentOrderDetails.splice(subRowIdx, 1);
          });
          currentOrderDetails[rowIdx].parentRowKey = null;
          currentOrderDetails[rowIdx].isSubRow = false;
          currentOrderDetails[rowIdx].parentRowId = null;
        }
      }

      if (value === PACK_TYPE_MULTI_SIZE && previousPackType !== PACK_TYPE_MULTI_SIZE) {
        currentOrderDetails[rowIdx].parentRowKey = currentOrderDetails[rowIdx].parentRowKey || generateParentRowKey();
        currentOrderDetails[rowIdx].isSubRow = false;
        currentOrderDetails[rowIdx].parentRowId = null;
      }

      const potentiallyDisabledKeys = ['ratio', 'packs', 'location', 'quantity'];
      potentiallyDisabledKeys.forEach((fieldKey) => {
        if (isFieldDisabled(currentOrderDetails[rowIdx], fieldKey)) {
          currentOrderDetails[rowIdx][fieldKey] = '';
        }
      });
      currentOrderDetails[rowIdx].total = calculateTotal(currentOrderDetails[rowIdx]);
    },
    [isFieldDisabled, calculateTotal],
  );

  const handleProductNoChange = useCallback(
    (currentOrderDetails, rowIdx, value) => {
      const row = currentOrderDetails[rowIdx];
      const rowKey = row.id || `row-${rowIdx}`;

      if (!value) {
        setManuallyClearedRows((prev) => new Set(prev).add(rowKey));
        setManuallySelectedProducts((prev) => {
          const newSet = new Set(prev);
          newSet.delete(rowKey);
          return newSet;
        });
        currentOrderDetails[rowIdx].colourWay = null;
      } else {
        setManuallyClearedRows((prev) => {
          const newSet = new Set(prev);
          newSet.delete(rowKey);
          return newSet;
        });
        // Mark this row as having a manually selected product
        setManuallySelectedProducts((prev) => new Set(prev).add(rowKey));
        currentOrderDetails[rowIdx].colourWay = null;
      }

      if (currentOrderDetails[rowIdx].packType === PACK_TYPE_MULTI_SIZE && !currentOrderDetails[rowIdx].isSubRow) {
        const { parentRowKey } = currentOrderDetails[rowIdx];

        if (parentRowKey) {
          currentOrderDetails.forEach((row, idx) => {
            if (row.isSubRow && row.parentRowKey === parentRowKey) {
              const subRowKey = row.id || `row-${idx}`;
              currentOrderDetails[idx] = {
                ...currentOrderDetails[idx],
                productNo: value,
                colourWay: null,
                skuNumber: currentOrderDetails[rowIdx].skuNumber,
              };
              if (!value) {
                setManuallyClearedRows((prev) => new Set(prev).add(subRowKey));
                setManuallySelectedProducts((prev) => {
                  const newSet = new Set(prev);
                  newSet.delete(subRowKey);
                  return newSet;
                });
              } else {
                setManuallyClearedRows((prev) => {
                  const newSet = new Set(prev);
                  newSet.delete(subRowKey);
                  return newSet;
                });
                // Mark subRow as having a manually selected product
                setManuallySelectedProducts((prev) => new Set(prev).add(subRowKey));
              }
            }
          });
        }
      }

      if (value) {
        getColourwaysForProduct(value, rowIdx);
      } else {
        setProductColourways((prev) => ({
          ...prev,
          [rowIdx]: [],
        }));
      }
    },
    [getColourwaysForProduct, setProductColourways],
  );

  const handleSizeChange = useCallback(
    (currentOrderDetails, rowIdx, value) => {
      currentOrderDetails[rowIdx].length = null;
      if (lengthEnabled) {
        const selectedSize = sizeOptions.find((option) => option.value === value);
        if (!selectedSize?.hasLength) {
          currentOrderDetails[rowIdx].length = null;
        } else if (lengthOptions.length === 1) {
          currentOrderDetails[rowIdx].length = lengthOptions[0].value;
        }
      }
    },
    [lengthEnabled, sizeOptions, lengthOptions],
  );

  const handleLocationChange = useCallback(
    (rowIdx, value) => {
      const selectedLocation = locationOptions.find((option) => option.value === value);
      if (selectedLocation?.distributionData) {
        setSelectedDistributionCenters((prev) => ({
          ...prev,
          [rowIdx]: selectedLocation.distributionData,
        }));
      } else {
        setSelectedDistributionCenters((prev) => {
          const newState = { ...prev };
          delete newState[rowIdx];
          return newState;
        });
      }
    },
    [locationOptions, setSelectedDistributionCenters],
  );

  const handlePriceCalculation = useCallback(
    (currentOrderDetails, rowIdx) => {
      const unit = Number(currentOrderDetails[rowIdx].unitPrice || 0);
      const disc = Number(currentOrderDetails[rowIdx].discount || 0);
      currentOrderDetails[rowIdx].priceAfterDiscount = calculatePriceAfterDiscount(unit, disc);
      currentOrderDetails[rowIdx].total = calculateTotal(currentOrderDetails[rowIdx]);
    },
    [calculatePriceAfterDiscount, calculateTotal],
  );

  const updateSubRowsForMultiSize = useCallback(
    (currentOrderDetails, rowIdx, colKey, value) => {
      const row = currentOrderDetails[rowIdx];
      if (row.packType !== PACK_TYPE_MULTI_SIZE || row.isSubRow) {
        return;
      }

      const { parentRowKey } = row;
      if (!parentRowKey) {
        return;
      }

      if (colKey === 'skuNumber') {
        currentOrderDetails.forEach((subRow, idx) => {
          if (subRow.isSubRow && subRow.parentRowId === parentRowKey) {
            currentOrderDetails[idx] = {
              ...currentOrderDetails[idx],
              skuNumber: value,
            };
          }
        });
      } else if (SHARED_FIELDS.includes(colKey)) {
        currentOrderDetails.forEach((subRow, idx) => {
          if (subRow.isSubRow && subRow.parentRowId === parentRowKey) {
            currentOrderDetails[idx] = {
              ...currentOrderDetails[idx],
              [colKey]: value,
            };

            if (colKey === 'quantity') {
              const updatedSubRow = currentOrderDetails[idx];
              const total = calculateTotal(updatedSubRow);
              currentOrderDetails[idx] = {
                ...currentOrderDetails[idx],
                total,
              };
            }
          }
        });
      }
    },
    [calculateTotal],
  );

  const updateOrderDetail = useCallback(
    (rowIdx, colKey, value) => {
      const currentOrderDetails = [...(creationState?.orderDetails || [])];
      if (!currentOrderDetails[rowIdx]) {
        return;
      }

      if (colKey === 'packType') {
        handlePackTypeChange(currentOrderDetails, rowIdx, value);
      } else if (colKey === 'productNo') {
        handleProductNoChange(currentOrderDetails, rowIdx, value);
      } else if (colKey === 'size') {
        handleSizeChange(currentOrderDetails, rowIdx, value);
      } else if (colKey === 'location') {
        handleLocationChange(rowIdx, value);
      }

      if (colKey !== 'packType') {
        if (colKey === 'ratio') {
          currentOrderDetails[rowIdx].quantity = value * currentOrderDetails[rowIdx].packs;
        }
        if (colKey === 'packs') {
          currentOrderDetails[rowIdx].quantity = value * currentOrderDetails[rowIdx].ratio;
        }
        currentOrderDetails[rowIdx][colKey] = value;
      }

      if (colKey === 'unitPrice' || colKey === 'discount') {
        handlePriceCalculation(currentOrderDetails, rowIdx);
      } else if (colKey === 'quantity' || colKey === 'packs' || colKey === 'ratio') {
        currentOrderDetails[rowIdx].total = calculateTotal(currentOrderDetails[rowIdx]);
      }

      if (colKey === 'packs') {
        updateSubRowsForMultiSize(currentOrderDetails, rowIdx, colKey, value);
        const currentRow = currentOrderDetails[rowIdx];
        if (currentRow.packType === PACK_TYPE_MULTI_SIZE && !currentRow.isSubRow) {
          const { parentRowKey } = currentRow;
          if (parentRowKey) {
            currentOrderDetails.forEach((subRow, idx) => {
              if (subRow.isSubRow && subRow.parentRowId === parentRowKey) {
                const subRatio = Number(subRow.ratio || 0);
                const parentPacks = Number(value || 0);
                currentOrderDetails[idx].quantity = subRatio * parentPacks;
                currentOrderDetails[idx].total = calculateTotal(currentOrderDetails[idx]);
              }
            });
          }
        }
      }

      if ((colKey === 'ratio' || colKey === 'packs') && currentOrderDetails[rowIdx].packType === PACK_TYPE_MULTI_SIZE) {
        updateParentRowQuantityForMultiSize(currentOrderDetails, rowIdx);
      }

      validateMultiSizeSubRowValues(currentOrderDetails);
      handlePOFieldChange('orderDetails', currentOrderDetails);
    },
    [
      creationState?.orderDetails,
      handlePOFieldChange,
      calculateTotal,
      handlePackTypeChange,
      updateParentRowQuantityForMultiSize,
      updateSubRowsForMultiSize,
      handleProductNoChange,
      handleSizeChange,
      handleLocationChange,
      handlePriceCalculation,
      validateMultiSizeSubRowValues,
    ],
  );

  const handleSelectChange = useCallback(
    (rowIdx, colKey) => (selectedOption) => {
      const isObjectIdField = OBJECT_ID_FIELDS.includes(colKey);
      let value = '';
      if (selectedOption) {
        value = selectedOption.value;
      } else if (isObjectIdField) {
        value = null;
      }
      updateOrderDetail(rowIdx, colKey, value);
    },
    [updateOrderDetail],
  );

  const handleNumberChange = useCallback(
    (rowIdx, colKey) => (e) => {
      const value = !e.target.value ? null : Number(e.target.value);
      updateOrderDetail(rowIdx, colKey, value);
    },
    [updateOrderDetail],
  );

  const handleTextChange = useCallback(
    (rowIdx, colKey) => (e) => {
      updateOrderDetail(rowIdx, colKey, e.target.value);
    },
    [updateOrderDetail],
  );

  const handleDateChange = useCallback(
    (rowIdx, colKey) => (date) => {
      updateOrderDetail(rowIdx, colKey, date);
    },
    [updateOrderDetail],
  );

  const getSelectOptions = (col, rowIdx) => {
    if (col.key === 'colourWay') {
      return productColourways[rowIdx] || [];
    }
    return col?.options || [];
  };

  const getSelectPlaceholder = (col, row, options, selectedValue, isColourwayDisabled, isLengthDisabled) => {
    let placeholder = `${t('COMMON_LABEL.SELECT', SELECT_LABEL)} ${col.label}`;

    if (col.key === 'colourWay') {
      if (isColourwayDisabled) {
        placeholder = t('CREATE_EDIT_PO.SELECT_PRODUCT_FIRST');
      } else if (options.length === 0 && row.productNo) {
        placeholder = t('CREATE_EDIT_PO.NO_COLOURWAYS_AVAILABLE');
      } else if (options.length === 1 && selectedValue) {
        placeholder = t('CREATE_EDIT_PO.AUTO_SELECTED_ONLY_OPTION');
      }
    } else if (col.key === 'length') {
      if (isLengthDisabled) {
        if (!lengthEnabled) {
          placeholder = t('CREATE_EDIT_PO.LENGTH_NOT_ENABLED');
        }
      } else if (options.length === 0) {
        placeholder = t('CREATE_EDIT_PO.NO_LENGTHS_AVAILABLE');
      }
    }

    return placeholder;
  };

  const renderSelectCell = (row, col, rowIdx, value) => {
    if (isPreviewMode) {
      let displayValue = '';

      if (col.key === 'productNo') {
        displayValue = row.productNo || value || EMPTY_DISPLAY;
      } else if (col.key === 'colourWay') {
        displayValue = row.colourwayName || row.colourWayName || value || EMPTY_DISPLAY;
      } else if (col.key === 'size') {
        displayValue = row.sizeName || EMPTY_DISPLAY;
        if (!row.sizeName && value) {
          const options = getSelectOptions(col, rowIdx);
          const valueStr = value?.toString();
          const selectedOption = options.find((opt) => opt.value?.toString() === valueStr || opt.value === value);
          displayValue = selectedOption ? selectedOption.label : valueStr || EMPTY_DISPLAY;
        }
      } else if (col.key === 'length') {
        displayValue = row.lengthName || EMPTY_DISPLAY;
        if (!row.lengthName && value) {
          const options = getSelectOptions(col, rowIdx);
          const valueStr = value?.toString();
          const selectedOption = options.find((opt) => opt.value?.toString() === valueStr || opt.value === value);
          displayValue = selectedOption ? selectedOption.label : valueStr || EMPTY_DISPLAY;
        }
      } else {
        const options = getSelectOptions(col, rowIdx);
        const selectedOption = options.find((opt) => opt.value === value);
        displayValue = selectedOption ? selectedOption.label : value || EMPTY_DISPLAY;
      }

      return <div className="form-control text-bordered">{displayValue}</div>;
    }

    const options = getSelectOptions(col, rowIdx);
    const selectedValue = options.find((opt) => opt.value === value) || null;
    const isColourwayDisabled = col.key === 'colourWay' && !row.productNo;
    const isLengthDisabled = col.key === 'length' && !lengthEnabled;
    const showMultiSizeAddButton = col.key === 'packType' && row[col.key] === PACK_TYPE_MULTI_SIZE && !isPreviewMode;

    const placeholder = getSelectPlaceholder(col, row, options, selectedValue, isColourwayDisabled, isLengthDisabled);

    const hasValidationError =
      (col.key === 'productNo' || col.key === 'packType') && !value && !row.isSubRow && creationState.validationErrors?.orderDetailsProducts;
    const errorClass = hasValidationError ? 'has-error' : '';

    return (
      <div className={`w-100 m-w-150 common-react-select mb-0 ${errorClass}`}>
        <div className="d-flex align-items-center">
          <div className="flex-grow-1 overflow-hidden">
            <Select
              name={col.key}
              options={options}
              className="common-select"
              classNamePrefix="common-select"
              placeholder={placeholder}
              value={selectedValue}
              onChange={handleSelectChange(rowIdx, col.key)}
              menuPosition="fixed"
              maxMenuHeight={150}
              isClearable
              isDisabled={isColourwayDisabled || isLengthDisabled || isFieldDisabled(row, col.key)}
              onMenuOpen={() => handleDropdownOpen(rowIdx, col.key)}
              onMenuClose={handleDropdownClose}
            />
          </div>
          {showMultiSizeAddButton && (
            <CustomTooltip tooltipText={t('CREATE_EDIT_PO.ADD_SIZE_SUB_ROW', 'Add size sub-row')} placement="top">
              <button
                type="button"
                className="btn btn-outline ml-2"
                aria-label={t('CREATE_EDIT_PO.ADD_SIZE_SUB_ROW', 'Add size sub-row')}
                onClick={() => addSubRow(rowIdx)}
                disabled={isFieldDisabled(row, col.key)}
              >
                <i className="icon-add" />
              </button>
            </CustomTooltip>
          )}
        </div>
        {hasValidationError && (
          <small className="error-message d-block mt-1">
            {(() => {
              if (col.key === 'productNo') {
                return t('CREATE_EDIT_PO.PRODUCT_IS_REQUIRED');
              }
              if (col.key === 'packType') {
                return t('CREATE_EDIT_PO.PACK_TYPE_IS_REQUIRED');
              }
              return t('CREATE_EDIT_PO.VALIDATION.FIELD_REQUIRED', { field: col.label }) || `${col.label} is required`;
            })()}
          </small>
        )}
      </div>
    );
  };

  const renderDateCell = (col, rowIdx, value) => {
    if (isPreviewMode) {
      const displayValue = value
        ? new Date(value).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })
        : EMPTY_DISPLAY;

      return <div className="form-control text-bordered">{displayValue}</div>;
    }

    return (
      <div className="po-date-picker position-relative mb-0">
        <CustomDatePicker
          keyProp={col.key + rowIdx}
          dateFormat="dd-MMM-yyyy"
          valueProp={value ? new Date(value) : null}
          popperPlace="top-end"
          name={col.key}
          placeholder={`${t('COMMON_LABEL.SELECT', SELECT_LABEL)} ${col.label}`}
          onChange={handleDateChange(rowIdx, col.key)}
          disable={isFieldDisabled(orderDetails[rowIdx], col.key)}
          portalId="date-picker-portal"
        />
      </div>
    );
  };

  const isLastParentRow = useCallback(
    (rowIdx) => {
      const currentOrderDetails = orderDetails || [];
      const currentRow = currentOrderDetails[rowIdx];

      if (currentRow?.isSubRow) return false;

      let lastParentRowIdx = -1;
      for (let i = currentOrderDetails.length - 1; i >= 0; i -= 1) {
        if (!currentOrderDetails[i]?.isSubRow) {
          lastParentRowIdx = i;
          break;
        }
      }

      return rowIdx === lastParentRowIdx;
    },
    [orderDetails],
  );

  const renderActionCell = (rowIdx) => {
    if (isPreviewMode) {
      return <div className="table-action-container" />;
    }

    const isDisabled = isSupplierLoggedIn || isFieldsDisabledByStatus();
    const currentRow = orderDetails[rowIdx];

    return (
      <div className="table-action-container">
        {isLastParentRow(rowIdx) && (
          <CustomTooltip tooltipText={t('COMMON_LABEL.ADD_NEW_ROW', 'Add new parent row')} placement="top">
            <button type="button" className="btn p-0" aria-label={t('COMMON_LABEL.ADD_NEW_ROW', 'Add new parent row')} onClick={addRow} disabled={isDisabled}>
              <i className="icon-Add f-20" />
            </button>
          </CustomTooltip>
        )}

        {orderDetails.length > 1 && (
          <CustomTooltip
            tooltipText={
              currentRow?.packType === PACK_TYPE_MULTI_SIZE
                ? t('COMMON_LABEL.DELETE_PARENT_AND_SUBROWS', 'Delete parent row and all sub-rows')
                : t('COMMON_LABEL.DELETE')
            }
            placement="top"
          >
            <button aria-label="button" type="button" className="btn p-0" onClick={() => deleteRow(rowIdx)} disabled={isDisabled}>
              <i className="icon-Delete f-20" />
            </button>
          </CustomTooltip>
        )}
      </div>
    );
  };

  const renderNumberCell = (col, rowIdx, value) => {
    if (isPreviewMode) {
      let displayValue = value ?? '';

      if (col.key.includes('Price') || col.key === 'total') {
        displayValue = value !== null && value !== undefined ? `${Number(displayValue).toFixed(2)}` : EMPTY_DISPLAY;
      } else if (col.key === 'discount') {
        displayValue = value !== null && value !== undefined ? `${Number(displayValue).toFixed(2)}%` : EMPTY_DISPLAY;
      } else {
        displayValue = displayValue || EMPTY_DISPLAY;
      }

      return <div className="form-control text-bordered">{displayValue}</div>;
    }

    return (
      <input
        type="number"
        name={col.key}
        value={value ?? ''}
        onChange={handleNumberChange(rowIdx, col.key)}
        className="form-control text-bordered"
        placeholder={`${t('COMMON_LABEL.ENTER', ENTER_LABEL)} ${col.label.replace(/\s*\([^)]*\)\s*/g, '').trim()}`}
        min={col?.min || undefined}
        max={col?.max || undefined}
        step={col.key.includes('Price') || col.key === 'total' ? '0.01' : '1'}
        disabled={isFieldDisabled(orderDetails[rowIdx], col.key)}
      />
    );
  };

  const renderTextCell = (col, rowIdx, value) => {
    return (
      <input
        type="text"
        name={col.key}
        value={value ?? ''}
        onChange={handleTextChange(rowIdx, col.key)}
        className="form-control text-bordered"
        disabled={isFieldDisabled(orderDetails[rowIdx], col.key)}
      />
    );
  };

  const renderCell = (row, col, rowIdx) => {
    const rawValue = row[col.key];
    const value = col.type === 'number' ? rawValue ?? '' : rawValue || '';

    if (col.type === 'select') {
      return renderSelectCell(row, col, rowIdx, value);
    }
    if (col.type === 'date') {
      return renderDateCell(col, rowIdx, value);
    }
    if (col.type === 'action') {
      return renderActionCell(rowIdx);
    }
    if (col.type === 'number') {
      return renderNumberCell(col, rowIdx, value);
    }

    return renderTextCell(col, rowIdx, value);
  };

  const renderSubRowCell = (subRow, col, rowIdx) => {
    const value = subRow[col.key] ?? '';

    if (SPANNED_FIELDS.includes(col.key)) {
      return null;
    }

    if (col.type === 'select') {
      const options = getSelectOptions(col, rowIdx);
      const selectedValue = options.find((opt) => opt.value === value) || null;
      const isLengthDisabled = col.key === 'length' && !lengthEnabled;
      const isSharedFieldDisabled = SHARED_FIELDS.includes(col.key);
      const subRowDropdownKey = `${subRow.originalIndex}-${col.key}`;
      const isActiveSubRowDropdown = activeDropdownKey === subRowDropdownKey;
      return (
        <div className={`w-100 m-w-150 common-react-select mb-0 ${isActiveSubRowDropdown ? 'z-index-8' : ''}`}>
          <Select
            name={col.key}
            options={options}
            className="common-select"
            classNamePrefix="common-select"
            placeholder={
              isSharedFieldDisabled && col.key !== 'location'
                ? `${col.label} (inherited from parent)`
                : `${t('COMMON_LABEL.SELECT', SELECT_LABEL)} ${col.label}`
            }
            value={selectedValue}
            onChange={(selectedOption) => {
              const newValue = selectedOption ? selectedOption.value : null;
              updateSubRow(subRow.originalIndex, col.key, newValue);
            }}
            menuPosition="fixed"
            maxMenuHeight={150}
            menuShouldBlockScroll
            menuShouldScrollIntoView
            isClearable={!isSharedFieldDisabled}
            isDisabled={isLengthDisabled || isFieldDisabled(subRow, col.key)}
            onMenuOpen={() => handleDropdownOpen(rowIdx, col.key)}
            onMenuClose={handleDropdownClose}
          />
        </div>
      );
    }

    if (col.type === 'number') {
      const isReadOnly = ['priceAfterDiscount', 'total'].includes(col.key);

      if (isReadOnly) {
        return renderNumberCell(col, subRow.originalIndex, value);
      }

      return (
        <input
          type="number"
          name={col.key}
          value={value ?? ''}
          onChange={(e) => {
            const newValue = !e.target.value ? null : Number(e.target.value);
            updateSubRow(subRow.originalIndex, col.key, newValue);
          }}
          className="form-control text-bordered"
          placeholder={`${t('COMMON_LABEL.ENTER', ENTER_LABEL)} ${col.label.replace(/\s*\([^)]*\)\s*/g, '').trim()}`}
          min={col?.min || undefined}
          max={col?.max || undefined}
          step={col.key.includes('Price') || col.key === 'total' ? '0.01' : '1'}
          disabled={isFieldDisabled(subRow, col.key)}
        />
      );
    }

    if (col.type === 'date') {
      if (isPreviewMode) {
        const displayValue = value
          ? new Date(value).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          : EMPTY_DISPLAY;

        return <div className="form-control text-bordered">{displayValue}</div>;
      }

      return (
        <div className="po-date-picker position-relative mb-0">
          <CustomDatePicker
            keyProp={col.key + subRow.originalIndex}
            dateFormat="dd-MMM-yyyy"
            valueProp={value ? new Date(value) : null}
            popperPlace="top-end"
            name={col.key}
            placeholder={`${t('COMMON_LABEL.SELECT', SELECT_LABEL)} ${col.label}`}
            onChange={(date) => updateSubRow(subRow.originalIndex, col.key, date)}
            disable={isFieldDisabled(subRow, col.key)}
            portalId="date-picker-portal"
          />
        </div>
      );
    }

    if (col.type === 'action') {
      return (
        <div className="table-action-container">
          <CustomTooltip tooltipText={t('COMMON_LABEL.DELETE_SUB_ROW', 'Delete this sub-row')} placement="top">
            <button
              aria-label="button"
              type="button"
              className="btn p-0"
              onClick={() => deleteSubRow(subRow.originalIndex)}
              disabled={isPreviewMode || isSupplierLoggedIn || isFieldsDisabledByStatus()}
            >
              <i className="icon-Delete f-20" />
            </button>
          </CustomTooltip>
        </div>
      );
    }

    if (col.type === 'text') {
      return (
        <input
          type="text"
          name={col.key}
          value={value ?? ''}
          onChange={(e) => updateSubRow(subRow.originalIndex, col.key, e.target.value)}
          className="form-control text-bordered"
          disabled={isFieldDisabled(subRow, col.key)}
        />
      );
    }

    return <div>--</div>;
  };

  const shouldShowAddressSection = useMemo(() => {
    const orderDetails = creationState?.orderDetails || [];
    const distributionCentres = poProductDetails?.companyDetails?.distributionCentres || [];

    const selectedLocations = new Set();
    orderDetails.forEach((order) => {
      if (order.location) {
        selectedLocations.add(order.location);
      }
    });

    const hasMultipleDifferentLocations = selectedLocations.size > 1;

    let isDifferentFromDefault = false;
    if (selectedLocations.size === 1 && distributionCentres.length > 0) {
      const selectedLocationName = Array.from(selectedLocations)[0];
      const defaultDistributionName = distributionCentres[0]?.distributionName;
      isDifferentFromDefault = selectedLocationName !== defaultDistributionName;
    }

    return hasMultipleDifferentLocations || isDifferentFromDefault;
  }, [poProductDetails?.companyDetails?.distributionCentres, creationState?.orderDetails]);

  const uniqueCenters = useMemo(() => {
    if (!shouldShowAddressSection) return [];

    const seen = new Set();
    const result = [];
    Object.values(selectedDistributionCenters || {}).forEach((dc) => {
      if (!dc) return;
      const key = dc.distributionName || [dc.buildingNumber, dc.streetAddress, dc.city, dc.postalCode, dc.countryName].filter(Boolean).join('|');
      if (key && !seen.has(key)) {
        seen.add(key);
        result.push(dc);
      }
    });
    return result;
  }, [selectedDistributionCenters, shouldShowAddressSection]);

  const formatAddress = useCallback((dc) => {
    if (!dc) return [];
    const addressFields = [];

    if (dc.buildingNumber) addressFields.push(dc.buildingNumber);
    if (dc.streetAddress) addressFields.push(dc.streetAddress);
    if (dc.city) addressFields.push(dc.city);
    if (dc.postalCode) addressFields.push(dc.postalCode);
    if (dc.countryName) addressFields.push(dc.countryName);

    return addressFields;
  }, []);

  return (
    <div className="inner-container order-details-section p-10 mb-8">
      <div className="section-template-title">{t('CREATE_EDIT_PO.ORDER_DETAILS')}</div>

      {creationState.validationErrors?.orderDetails && (
        <div className="alert alert-danger mb-3">
          <small className="error-message">{creationState.validationErrors.orderDetails}</small>
        </div>
      )}

      <div className="rp-scroll-container">
        <table id="PO_OrderDetails" className="table common-custom-table fixed-header mb-0">
          <thead className="thead-sticky">
            <tr>
              {columns.map((col) => {
                let widthClass = 'w-224px';
                if (col.key === 'action') {
                  widthClass = 'w-80px';
                } else if (['skuNumber', 'packs', 'ratio', 'quantity', 'unitPrice', 'discount', 'priceAfterDiscount', 'total'].includes(col.key)) {
                  widthClass = 'w-120px';
                }

                const positionClass = col.key === 'packType' ? 'first-fix-col' : actionCol(col.key);

                return (
                  <th key={col.key} className={`${widthClass} ${positionClass}`}>
                    {col.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="tbody-sticky">
            {getUIDisplayData.map((row, rowIdx) => (
              <React.Fragment key={row.id || rowIdx}>
                <tr data-row-index={row.originalIndex}>
                  {columns.map((col) => {
                    const rowSpan = getRowSpan(row);
                    const shouldSpan = SPANNED_FIELDS.includes(col.key) && row.packType === PACK_TYPE_MULTI_SIZE && rowSpan > 1;
                    const dropdownKey = `${row.originalIndex}-${col.key}`;
                    const isActiveDropdown = activeDropdownKey === dropdownKey;
                    return (
                      <td
                        key={col.key}
                        className={`${col.key === 'packType' ? `first-fix-col ${isActiveDropdown ? 'z-index-9' : ''}` : actionCol(col.key)}`}
                        rowSpan={shouldSpan ? rowSpan : 1}
                      >
                        {renderCell(row, col, row.originalIndex)}
                      </td>
                    );
                  })}
                </tr>

                {row.subRows &&
                  row.subRows.map((subRow) => (
                    <tr key={subRow.id} className="sub-row">
                      {columns.map((col) => {
                        const shouldSkip = SPANNED_FIELDS.includes(col.key);

                        if (shouldSkip) {
                          return null;
                        }

                        return (
                          <td key={col.key} className={`${actionCol(col.key)}`}>
                            {renderSubRowCell(subRow, col, subRow.originalIndex)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {shouldShowAddressSection && uniqueCenters.length > 0 && (
        <>
          <div className="section-template-title">{t('CREATE_EDIT_PO.MULTI_DESTINATION_ADDRESS')}</div>
          <div className="row">
            {uniqueCenters.map((center, index) => (
              <div key={center?.distributionName || index} className="col-md-3">
                <div className="p-10">
                  <div>
                    <span className="text-sm font-weight-bold">{center?.distributionName || ''}</span>
                  </div>
                  <div className="text-sm">
                    {formatAddress(center).map((addressLine) => (
                      <div key={`${center?.distributionName || 'center'}-${addressLine}`}>{addressLine}</div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

OrderDetailsForPO.propTypes = {
  isPreviewMode: PropTypes.bool,
  highlightProductInfo: PropTypes.shape({
    productNumber: PropTypes.string,
    colourway: PropTypes.string,
    size: PropTypes.string,
    length: PropTypes.string,
  }),
  isEditMode: PropTypes.bool,
};

OrderDetailsForPO.defaultProps = {
  isPreviewMode: false,
  highlightProductInfo: null,
  isEditMode: false,
};

export default React.memo(OrderDetailsForPO);
